import React, { useCallback, useState } from 'react';
import { faDoorOpen } from '@fortawesome/free-solid-svg-icons/faDoorOpen';
import { FetchResult } from '@apollo/client';

import Button, {
  BUTTON_SIZES,
  BUTTON_TYPES,
  BUTTON_WIDTHS
} from 'buyside/components/presentation/Button/button';
import Modal from 'buyside/components/presentation/Modal/modal';

import {
  CloseQuickPositionMutation,
  Maybe,
  User,
  useCloseQuickPositionMutation,
  useMainNavigationCurrentUserQuery
} from 'interfaces/graphql-typings';

const CloseQuickPositionModal: React.FC<{
  assetId: string;
  currencyCode: Maybe<string>;
  forTable?: boolean;
  latestPrice: Maybe<number>;
  quickPositionId: string;
  symbol: Maybe<string>;
}> = ({ assetId, currencyCode, forTable, latestPrice, quickPositionId, symbol }) => {
  const [modalOpen, setModalOpen] = useState(false);

  const handleOpenModal = (): void => setModalOpen(true);
  const handleCloseModal = (): void => setModalOpen(false);

  const { data: userData } = useMainNavigationCurrentUserQuery();

  const [
    closeQuickPosition,
    { loading: closeQuickPositionLoading, error }
  ] = useCloseQuickPositionMutation({
    update: (cache, { data }) => {
      if (forTable) {
        const updatedQuickPosition = data?.closeQuickPosition?.quickPosition;

        if (!updatedQuickPosition) return;

        const { id, closedAt, closingPrice } = updatedQuickPosition;

        cache.modify({
          id: `QuickPosition:${id}`,
          fields: {
            closedAt: () => closedAt,
            closingPrice: () => closingPrice
          }
        });

        cache.modify<User>({
          id: `User:${userData?.currentUser?.id}`,
          fields: {
            openQuickPositions: (previousPositions, { readField }) =>
              previousPositions.filter(position => readField('id', position) !== id)
          }
        });
      }

      return cache.modify({
        id: `Asset:${assetId}`,
        fields: {
          currentUserQuickPosition: () => null
        }
      });
    }
  });

  const handleCloseQuickPosition = (): Promise<FetchResult<CloseQuickPositionMutation>> =>
    closeQuickPosition({ variables: { quickPositionId } });

  const renderOpenModalButton = useCallback(
    () => (
      <Button
        faIcon={faDoorOpen}
        onClick={handleOpenModal}
        size={BUTTON_SIZES.TINY}
        text={forTable ? 'Close' : 'Close My Position'}
        type={forTable ? BUTTON_TYPES.LINK : BUTTON_TYPES.SECONDARY}
        width={BUTTON_WIDTHS.FULL}
      />
    ),
    [forTable]
  );

  if (error) throw error;

  return (
    <Modal
      isOpen={modalOpen}
      onClose={handleCloseModal}
      renderOpenModalButton={renderOpenModalButton}
      title="Close My Position"
    >
      <p>
        {latestPrice ? (
          <>
            You are about to close your position on <strong>{symbol}</strong> at the previous day
            closing price of{' '}
            <strong>
              {latestPrice} {currencyCode}
            </strong>
            . If you would like to close at today’s closing price, come back tomorrow.
          </>
        ) : (
          <>
            Latest price pending. Please check back later to close your position on{' '}
            <strong>{symbol}</strong>
          </>
        )}
      </p>
      <div>
        <Button
          buttonType={BUTTON_TYPES.BUTTON}
          disabled={!latestPrice}
          isLoading={closeQuickPositionLoading}
          onClick={handleCloseQuickPosition}
          text="Submit"
          type={BUTTON_TYPES.PRIMARY}
        />
        <Button
          buttonType={BUTTON_TYPES.BUTTON}
          disabled={closeQuickPositionLoading}
          onClick={handleCloseModal}
          text="Cancel"
        />
      </div>
    </Modal>
  );
};

export default CloseQuickPositionModal;
